import { z } from "zod";
import type { FiltersTypes } from "@common/types/filters/general";
import {
    SchedulesReportLayout,
    SchedulesReportRows,
    SchedulesReportRevenueSubMode,
    SchedulesReportMethod,
    SCHEDULES_REPORT_LAYOUT_LABELS,
} from "@common/types/reports/schedules-report";

import { resolveLayoutChange } from "../schedulesReport/schedulesReportFilterRules";
import {
    DEFAULT_SCHEDULES_REPORT_LAYOUT,
    DEFAULT_SCHEDULES_REPORT_ROWS,
    DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
    DEFAULT_SCHEDULES_REPORT_METHOD,
} from "../schedulesReport/useSchedulesReportParams";
import { AccountingReportCycleTotal } from "@common/constants";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    reportPackageRowSchema,
    reportPackageReportSettingSchema,
} from "@common/schemas/domains/accounting/report-package.schema";
import { toReportPackageSheetNameKey } from "@common/helpers/report-package-sheet-name";
import {
    isArAgingReportPackageType,
    isAccrualReportPackageType,
    ReportPackageRowReportType,
} from "@common/types/reports/report-package";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { ACCOUNTING_REPORT_FILTER_DEFINITIONS } from "../accountingReportFilterDefinitions";
import type { AccountingReportFilters } from "../types";
import {
    mapArAgingFilters,
    mapGeneralLedgerFilters,
    mapSchedulesReportFilters,
} from "../accountingReportFilters.lib";

export type ReportPackageRow = Omit<
    z.infer<typeof reportPackageRowSchema>,
    "ledgerId" | "reportType" | "filters"
> &
    z.infer<typeof reportPackageReportSettingSchema> & {
        id: string;
        reportType: ReportPackageRowReportType;
        layout?: SchedulesReportLayout;
        rows?: SchedulesReportRows;
        revenueSubMode?: SchedulesReportRevenueSubMode;
        method?: SchedulesReportMethod;
        basis: AccountingLedgerBasis;
        isTitleEdited: boolean;
        usesPackageDates: boolean;
        filters?: AccountingReportFilters & Pick<FiltersTypes, "agingBuckets">;
    };

export type ReportPackageRowOptions = Pick<
    ReportPackageRow,
    | "cycle"
    | "comparisons"
    | "comparisonDiffs"
    | "ratioTypes"
    | "layout"
    | "rows"
    | "revenueSubMode"
    | "method"
>;

const CLASS_CYCLE_REPORT_TYPES = new Set<ReportPackageRowReportType>([
    AccountingReportType.SCHEDULES,
    AccountingReportType.PROFIT_LOSS,
    AccountingReportType.BALANCE_SHEET,
    AccountingReportType.CASH_FLOW_STATEMENT,
]);

export function getReportPackageRowOptionDefaults(
    reportType: ReportPackageRowReportType,
    layout?: SchedulesReportLayout,
): ReportPackageRowOptions {
    if (reportType === AccountingReportType.SCHEDULES) {
        const next = resolveLayoutChange({
            nextLayout: layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT,
            cycle: undefined,
        });
        return {
            ...next,
            comparisons: undefined,
            comparisonDiffs: undefined,
            ratioTypes: undefined,
            rows: DEFAULT_SCHEDULES_REPORT_ROWS,
            revenueSubMode: DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
            method: DEFAULT_SCHEDULES_REPORT_METHOD,
        };
    }
    const defaultCycle =
        ACCOUNTING_REPORT_FILTER_DEFINITIONS[reportType]?.defaultCycle;
    return {
        layout: undefined,
        rows: undefined,
        revenueSubMode: undefined,
        method: undefined,
        cycle:
            defaultCycle === AccountingReportCycleTotal.TOTAL
                ? undefined
                : defaultCycle,
        comparisons: undefined,
        comparisonDiffs: undefined,
        ratioTypes: undefined,
    };
}

export function reportPackageSupportsClassCycle(
    reportType: ReportPackageRowReportType,
): boolean {
    return CLASS_CYCLE_REPORT_TYPES.has(reportType);
}

export function getDefaultReportPackageRowBasis(
    reportType: ReportPackageRowReportType,
): AccountingLedgerBasis {
    return isAccrualReportPackageType(reportType)
        ? AccountingLedgerBasis.accruals
        : AccountingLedgerBasis.cash;
}

export function buildReportPackageRowTitle({
    reportType,
    takenTitles,
    layout,
}: {
    reportType: ReportPackageRowReportType;
    takenTitles: string[];
    layout?: SchedulesReportLayout;
}): string {
    const label =
        reportType === AccountingReportType.SCHEDULES
            ? SCHEDULES_REPORT_LAYOUT_LABELS[
                  layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT
              ]
            : ACCOUNTING_REPORT_LABELS[reportType];
    const takenKeys = new Set(takenTitles.map(toReportPackageSheetNameKey));
    const isTaken = (title: string) =>
        takenKeys.has(toReportPackageSheetNameKey(title));
    if (!isTaken(label)) {
        return label;
    }
    let number = 2;
    while (isTaken(`${label} (${number})`)) {
        number += 1;
    }
    return `${label} (${number})`;
}

export function getReportPackageRowRequestDates(row: ReportPackageRow) {
    if (row.usesPackageDates) {
        return {};
    }
    return { startDate: row.startDate, endDate: row.endDate };
}

export function hasCompleteReportPackageRowDates(row: ReportPackageRow) {
    return (
        row.usesPackageDates ||
        (row.startDate !== undefined && row.endDate !== undefined)
    );
}

export function getCustomReportPackageRowDatesPatch({
    row,
    packageDateRange,
}: {
    row: ReportPackageRow;
    packageDateRange: DateOnlyDateRange;
}): Partial<ReportPackageRow> {
    const endDate = row.endDate ?? packageDateRange.end;
    return {
        usesPackageDates: false,
        startDate: isArAgingReportPackageType(row.reportType)
            ? endDate
            : (row.startDate ?? packageDateRange.start),
        endDate,
    };
}

export function getReportPackageRowTypeDatePatch({
    row,
    reportType,
    packageDateRange,
}: {
    row: ReportPackageRow;
    reportType: ReportPackageRowReportType;
    packageDateRange: DateOnlyDateRange;
}): Partial<ReportPackageRow> {
    if (
        isArAgingReportPackageType(row.reportType) ===
        isArAgingReportPackageType(reportType)
    ) {
        return {};
    }
    if (row.usesPackageDates) {
        return { startDate: undefined, endDate: undefined };
    }
    const endDate = row.endDate ?? packageDateRange.end;
    const startDate =
        isArAgingReportPackageType(reportType) ||
        (packageDateRange.start && endDate && packageDateRange.start > endDate)
            ? endDate
            : packageDateRange.start;
    return { startDate, endDate };
}

export function getReportPackageRowEntityFilters({
    row,
    previousEntityId,
    entityId,
}: {
    row: ReportPackageRow;
    previousEntityId?: number;
    entityId?: number;
}): AccountingReportFilters | undefined {
    if (
        (row.reportType !== AccountingReportType.GENERAL_LEDGER &&
            !isArAgingReportPackageType(row.reportType)) ||
        previousEntityId === entityId ||
        !row.filters
    ) {
        return row.filters;
    }
    if (isArAgingReportPackageType(row.reportType)) {
        return { ...row.filters, accountIds: undefined };
    }
    return {
        ...row.filters,
        accountCodes: undefined,
        accountIds: undefined,
        subaccountIds: undefined,
    };
}

export function getReportPackageRowRequestFilters(row: ReportPackageRow) {
    if (isArAgingReportPackageType(row.reportType)) {
        const filters = mapArAgingFilters({
            ...row.filters,
            customerId: row.filters?.counterpartiesIds?.[0],
        });

        if (row.reportType === AccountingReportType.AR_AGING_DETAIL) {
            return {
                filters: { ...filters, buckets: row.filters?.agingBuckets },
            };
        }

        return { filters };
    }
    if (row.reportType === AccountingReportType.SCHEDULES) {
        return { filters: mapSchedulesReportFilters(row.filters) };
    }
    if (row.reportType === AccountingReportType.GENERAL_LEDGER) {
        return { filters: mapGeneralLedgerFilters(row.filters) };
    }
    const filterDefinition =
        ACCOUNTING_REPORT_FILTER_DEFINITIONS[row.reportType];
    if (!filterDefinition) {
        return {};
    }
    return {
        filters: {
            classes: row.filters?.classes,
            classFilterType: row.filters?.classFilterType,
            ...(filterDefinition.filterProps.showCounterpartyFilter
                ? {
                      counterpartiesIds: row.filters?.counterpartiesIds,
                      withEmptyOrUnverifiedCounterparty:
                          row.filters?.withEmptyOrUnverifiedCounterparty,
                      hasCounterparty: row.filters?.hasCounterparty,
                  }
                : {}),
        },
    };
}
